import React, { Dispatch, ReactNode, SetStateAction, useCallback, useMemo } from 'react';
import { createCtx } from '@shared/util/context';
import { useCurrentBusinessId, useCurrentUserId } from '@shared/util/hooks';
import { useLocalStorage } from '@shared/util/storage/useLocalStorage';
import { useMigratePinnedChats } from '../migratePinnedChats/useMigratePinnedChats';

// favorites are an ordered list of channel ids, persisted in localstorage

export type FavoriteChannels = number[];

interface FavoriteChannelsContext {
  favorites: FavoriteChannels;
  setFavorites: Dispatch<SetStateAction<FavoriteChannels>>;
  addFavorite: (channelId: number) => void;
  removeFavorite: (channelId: number) => void;
  toggleFavorite: (channelId: number) => void;
}

const storagePrefix = 'FavoriteChannels.v1';
const defaultValue: FavoriteChannels = [];

const [useFavoriteChannels, Provider] = createCtx<FavoriteChannelsContext>();
export { useFavoriteChannels };

export function FavoriteChannelsProvider({ children }: { children: ReactNode }) {
  const userId = useCurrentUserId();
  const businessId = useCurrentBusinessId();
  const storageKey = `${storagePrefix}.${userId}.${businessId}`;
  const [favorites, setFavorites] = useLocalStorage<FavoriteChannels>(storageKey, defaultValue);

  useMigratePinnedChats({ setFavorites });

  const addFavorite = useCallback(
    (channelId: number) => {
      setFavorites(prev => [channelId, ...prev]);
    },
    [setFavorites],
  );

  const removeFavorite = useCallback(
    (channelId: number) => {
      setFavorites(prev => prev.filter(id => id !== channelId));
    },
    [setFavorites],
  );

  const toggleFavorite = useCallback(
    (channelId: number) => {
      setFavorites(prev => {
        if (prev.includes(channelId)) {
          return prev.filter(id => id !== channelId);
        } else {
          return [...prev, channelId];
        }
      });
    },
    [setFavorites],
  );

  const value = useMemo(
    () => ({ favorites, setFavorites, addFavorite, removeFavorite, toggleFavorite }),
    [favorites, setFavorites, addFavorite, removeFavorite, toggleFavorite],
  );

  return <Provider value={value}>{children}</Provider>;
}
